import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL;

export default function AffiliateReports() {
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [yearly, setYearly] = useState<any[]>([]);
  const [balances, setBalances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const [yr, bal] = await Promise.all([
      fetch(`${API}/admin/affiliates/reports/yearly?year=${year}`, { credentials: 'include' }).then((r) => r.json() as Promise<{ success: boolean; data: any[] }>),
      fetch(`${API}/admin/affiliates/reports/balances`, { credentials: 'include' }).then((r) => r.json() as Promise<{ success: boolean; data: any[] }>),
    ]);
    if (yr.success) setYearly(yr.data || []);
    if (bal.success) setBalances(bal.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const crossedThreshold = yearly.filter((r) => r.crossed_1099_threshold);
  const totalPaidYear = yearly.reduce((s, r) => s + Number(r.total_paid_in_year || 0), 0);
  const totalNegative = balances.filter((b) => b.running_balance < 0);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Reports</h2>

      {/* Year selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <label className="text-sm font-medium mr-3">Year:</label>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="border rounded px-3 py-1.5">
          {Array.from({ length: 7 }, (_, i) => new Date().getUTCFullYear() - i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs text-gray-500">Total paid in {year}</div>
          <div className="text-2xl font-bold font-mono">${totalPaidYear.toFixed(2)}</div>
          <div className="text-xs text-gray-500 mt-1">{yearly.length} affiliate(s) tracked</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs text-gray-500">$600 Threshold (1099 needed)</div>
          <div className="text-2xl font-bold text-orange-600">{crossedThreshold.length}</div>
          <div className="text-xs text-gray-500 mt-1">Affiliates crossed threshold</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs text-gray-500">Negative Running Balances</div>
          <div className="text-2xl font-bold text-red-600">{totalNegative.length}</div>
          <div className="text-xs text-gray-500 mt-1">Affiliates owe back</div>
        </div>
      </div>

      {/* $600 threshold list */}
      {crossedThreshold.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium text-orange-900">1099-NEC Required ({year})</h3>
            <a
              href={`${API}/admin/affiliates/reports/1099-packet?year=${year}`}
              download
              className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 whitespace-nowrap"
              title="Downloads a CSV with full TIN + address for all affiliates over $600 (audited)"
            >
              Download 1099 Packet (CSV)
            </a>
          </div>
          <p className="text-sm text-orange-800 mb-3">These affiliates have been paid $600 or more in {year}. The CSV download includes the full decrypted TIN and address for your accountant — every download is logged to the audit trail.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left">
              <th className="p-2">Name (Legal)</th>
              <th className="p-2">TIN Type / Last 4</th>
              <th className="text-right p-2">YTD Paid</th>
              <th className="text-right p-2">Payments</th>
            </tr></thead>
            <tbody>
              {crossedThreshold.map((r) => (
                <tr key={r.affiliate_id} className="border-t border-orange-200">
                  <td className="p-2">{r.legal_name || r.display_name}</td>
                  <td className="p-2 text-xs">{r.tin_type || '—'} {r.tin_last_4 ? `••••${r.tin_last_4}` : ''}</td>
                  <td className="p-2 text-right font-mono font-bold">${Number(r.total_paid_in_year).toFixed(2)}</td>
                  <td className="p-2 text-right">{r.payment_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Full yearly table */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b font-medium">Yearly Totals ({year})</div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3">Affiliate</th>
              <th className="text-left p-3">Legal / TIN</th>
              <th className="text-right p-3">YTD Paid</th>
              <th className="text-right p-3">Payments</th>
              <th className="p-3">1099?</th>
            </tr>
          </thead>
          <tbody>
            {yearly.map((r) => (
              <tr key={r.affiliate_id} className="border-b">
                <td className="p-3">{r.display_name}</td>
                <td className="p-3 text-xs">{r.legal_name || '—'} {r.tin_last_4 && `(${r.tin_type} ••${r.tin_last_4})`}</td>
                <td className="p-3 text-right font-mono">${Number(r.total_paid_in_year).toFixed(2)}</td>
                <td className="p-3 text-right">{r.payment_count}</td>
                <td className="p-3">{r.crossed_1099_threshold ? <span className="text-orange-600 font-bold">YES</span> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Balances */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b font-medium">Running Balances (all affiliates)</div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3">Affiliate</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Method</th>
              <th className="text-right p-3">Total Due</th>
              <th className="text-right p-3">Total Paid</th>
              <th className="text-right p-3">Balance</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((b) => (
              <tr key={b.affiliate_id} className={`border-b ${b.running_balance < 0 ? 'bg-red-50' : ''}`}>
                <td className="p-3">{b.display_name}</td>
                <td className="p-3 text-xs">{b.status}</td>
                <td className="p-3 text-xs">{b.payment_method_preference}</td>
                <td className="p-3 text-right font-mono">${Number(b.total_due).toFixed(2)}</td>
                <td className="p-3 text-right font-mono">${Number(b.total_paid).toFixed(2)}</td>
                <td className={`p-3 text-right font-mono font-bold ${b.running_balance < 0 ? 'text-red-600' : ''}`}>
                  ${Number(b.running_balance).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
